/**
 * DashboardLayout
 *
 * Top-level authenticated layout: header, tabs, modals. Owns the dashboard
 * UI state and player-management hooks and threads them through to the
 * relevant children.
 */

import { useCallback, type ReactNode } from 'react';
import { ConfirmModal } from '../../ui/ConfirmModal';
import { useAuthContext } from '../../contexts/AuthContext';
import { useGameContext } from '../../contexts/GameContext';
import { useDashboardState } from '../../hooks/useDashboardState';
import { usePlayerManagement } from '../../hooks/usePlayerManagement';
import { GamePanel } from '../GamePanel';
import { CreateGameModal } from '../CreateGameModal';
import { DiscordAuthModal } from '../DiscordAuthModal';
import { GamesListView } from '../GamesListView';
import { ContentLibraryView } from '../content-library';
import { GLOBAL_DATA_TABS, GlobalDataView } from '../global-data';
import { AdminManagementView } from '../AdminManagementView';
import { UserMenu } from '../UserMenu';
import { ProfileModal } from '../ProfileModal';
import { DiscordStatusWidget } from '../DiscordStatusWidget';
import { PlayerRegistrationModal } from '../fpr';
import { DashboardTabs } from './DashboardTabs';
import { SeedTestPlayersModal } from './SeedTestPlayersModal';

export function DashboardLayout() {
  const { username, logout, isSuperAdmin } = useAuthContext();
  const { selectedGame, selectGame } = useGameContext();
  const dashboard = useDashboardState();
  const playerManagement = usePlayerManagement();

  const handleSelectGameFromList = useCallback((game: typeof selectedGame) => {
    if (game) {
      selectGame(game);
      dashboard.setShowGamesListView(false);
    }
  }, [selectGame, dashboard]);

  const view = dashboard.showContentLibraryView ? 'content-library'
             : dashboard.showGlobalDataView      ? 'global-data'
             : dashboard.showGamesListView       ? 'games-list'
             :                                    'main';

  let headerLeft: ReactNode;
  let headerCenter: ReactNode;

  switch (view) {
    case 'content-library':
      headerLeft  = <button className="btn btn--outline btn--slate" onClick={() => dashboard.setShowContentLibraryView(false)}>← Back</button>;
      headerCenter = <h2 className="m-0 d-flex align-items-center text-white text-uppercase ff-display">Content Library</h2>;
      break;
    case 'global-data':
      headerLeft  = <button className="btn btn--outline btn--slate" onClick={() => dashboard.setShowGlobalDataView(false)}>← Back</button>;
      headerCenter = <h2 className="m-0 d-flex align-items-center text-white text-uppercase ff-display">Global Data</h2>;
      break;
    case 'games-list':
      headerLeft  = <button className="btn btn--outline btn--slate" onClick={() => dashboard.setShowGamesListView(false)}>← Back</button>;
      headerCenter = <h2 className="m-0 d-flex align-items-center text-white text-uppercase ff-display">All Games</h2>;
      break;
    default:
      headerLeft  = <h1 className="m-0 d-flex align-items-center text-white text-uppercase fs-base ff-display">SWTORBIT Admin</h1>;
      headerCenter = <GamePanel onViewAllGames={() => dashboard.setShowGamesListView(true)} onCreateGame={() => dashboard.setShowCreateGameModal(true)} />;
  }

  return (
    <div className="d-flex flex-column">
      <header className="navbar p-sm bg-midnight border-bottom border-navy d-flex justify-content-between align-items-stretch gap-base">
        <div className="header-left flex-1 d-flex align-items-stretch">{headerLeft}</div>
        <div className="header-center d-flex align-items-stretch gap-xs flex-1 justify-content-center">{headerCenter}</div>
        <div className="header-right flex-1 justify-content-end d-flex align-items-stretch gap-xs">
          <DiscordStatusWidget onOpenDiscordModal={() => dashboard.setShowDiscordAuthModal(true)} />
          <UserMenu
            username={username}
            logout={logout}
            onOpenContentLibrary={() => dashboard.setShowContentLibraryView(true)}
            onOpenProfile={() => dashboard.setShowProfileModal(true)}
            isSuperAdmin={isSuperAdmin}
            onOpenGlobalData={GLOBAL_DATA_TABS.length > 0 ? () => dashboard.setShowGlobalDataView(true) : undefined}
            onOpenAdminManagement={() => dashboard.setShowAdminManagementView(true)}
          />
        </div>
      </header>

      {dashboard.showContentLibraryView ? (
        <ContentLibraryView />
      ) : isSuperAdmin && dashboard.showGlobalDataView ? (
        <GlobalDataView />
      ) : dashboard.showGamesListView ? (
        <GamesListView
          onSelectGame={handleSelectGameFromList}
        />
      ) : isSuperAdmin && dashboard.showAdminManagementView ? (
        <AdminManagementView onBack={() => dashboard.setShowAdminManagementView(false)} />
      ) : selectedGame ? (
        <DashboardTabs
          activeTab={dashboard.activeTab}
          onTabChange={dashboard.setActiveTab}
          playerManagement={playerManagement}
          onEditRegistration={dashboard.setRegModalPlayer}
          onOpenSeedModal={() => dashboard.setShowSeedModal(true)}
        />
      ) : null}

      <CreateGameModal
        isOpen={dashboard.showCreateGameModal}
        onClose={() => dashboard.setShowCreateGameModal(false)}
      />

      <DiscordAuthModal
        isOpen={dashboard.showDiscordAuthModal}
        onClose={() => dashboard.setShowDiscordAuthModal(false)}
      />

      <ProfileModal isOpen={dashboard.showProfileModal} onClose={() => dashboard.setShowProfileModal(false)} />

      <ConfirmModal
        isOpen={playerManagement.banConfirmState.show}
        onConfirm={playerManagement.confirmBan}
        onCancel={playerManagement.cancelBan}
        title="Ban Player"
        message="Are you sure you want to ban this player? They will be disconnected and unable to rejoin."
        confirmText="Ban Player"
        variant="danger"
      />

      {dashboard.regModalPlayer && selectedGame && (
        <PlayerRegistrationModal
          player={dashboard.regModalPlayer}
          roomCode={selectedGame.roomCode}
          onClose={() => dashboard.setRegModalPlayer(null)}
        />
      )}

      {selectedGame && (
        <SeedTestPlayersModal
          isOpen={dashboard.showSeedModal}
          onClose={() => dashboard.setShowSeedModal(false)}
          roomCode={selectedGame.roomCode}
        />
      )}
    </div>
  );
}
